import { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  message,
  Modal,
} from "antd";
import "./addAuditForms.css";
import secureLocalStorage from "react-secure-storage";
import { useLocation } from "react-router-dom";
import {
  addAuditFormThunk,
  editAuditFormThunk,
} from "../../../store/thunks/auditFormsThunk";
import AddEditForm from "../../../components/molecules/Forms/addEditForm";
import { useDispatch } from "react-redux";

const { Option } = Select;

const AddAuditForm = ({ editData, processes, onSuccess }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const slsRole = secureLocalStorage.getItem("role");
  const slsLIUId = secureLocalStorage.getItem("lIUId");
  const location = useLocation();
  const [errors, setErrors] = useState([]);
  const [isWeightageForm, setIsWeightageForm] = useState(false);

  const handleCheckboxChange = (e) => {
    setIsWeightageForm(e.target.checked); // Update state when checkbox is toggled
  };

  const [parameters, setParameters] = useState([]);
  const [error, setError] = useState("");

  const handleAddParameter = () => {
    const newParameter = {
      type: "radio-group",
      displayText: "",
      selectedValue: null,
      weightage: null,
      critical: false,
      mandatory: false,
    };

    setParameters([...parameters, newParameter]);
    setError(""); // Clear the error when adding a parameter

    // Update form values to include the new parameter
    const updatedValues = {
      ...form.getFieldsValue(),
      [`type_${parameters.length}`]: newParameter.type,
      [`displayText_${parameters.length}`]: newParameter.displayText,
      [`weightage_${parameters.length}`]: newParameter.weightage,
      [`critical_${parameters.length}`]: newParameter.critical || false,
      [`required_${parameters.length}`]: newParameter.required || false,
    };
    form.setFieldsValue(updatedValues);
  };

  const handleRemoveParameter = (index) => {
    const updatedParameters = parameters.filter((_, i) => i !== index);
    setParameters(updatedParameters);
    // Update form values to reflect the removal
    const updatedValues = {};
    updatedParameters.forEach((param, i) => {
      updatedValues[`type_${i}`] = param.type;
      updatedValues[`displayText_${i}`] = param.displayText;
      updatedValues[`weightage_${i}`] = param.weightage;
      updatedValues[`critical_${i}`] = param.critical || false;
      updatedValues[`required_${i}`] = param.required || false;
    });
    form.setFieldsValue(updatedValues);
  };

  const handleFieldChange = (value, index, field) => {
    const updatedParameters = [...parameters];
    updatedParameters[index][field] = value;
    setParameters(updatedParameters);
  };

  const { confirm } = Modal;
  const onSubmit = (formContent) => {
    if (parameters.length === 0) {
      setError("Please add at least one parameter.");
      return Promise.reject();
    }
    return new Promise((resolve, reject) => {
      confirm({
        title: "Confirm!",

        content: (
          <>
            <p>
              Please review your selected or entered information carefully
              before submitting the form, as changes may not be possible once
              it's submitted.
            </p>
            <p>Are you sure you want to submit the form?</p>
          </>
        ),

        okText: "Yes",
        cancelText: "No",
        className: "revokeConfirmModal",
        okButtonProps: {
          className: "defaultBtn",
        },
        cancelButtonProps: {
          className: "defaultBtn defaultGreyBtn",
        },

        async onOk() {
          try {
            setError("");
            const selectedProcess = processes.find(
              (process) => process.value === formContent.process,
            );
            const payload = {
              ...(editData?.key && { id: editData.key }),
              slsRole,
              hasWeightage:
                formContent?.isWeightageForm === true ? "TRUE" : "FALSE",
              formName: selectedProcess && selectedProcess?.label,
              process: selectedProcess && selectedProcess?.value,
              activeAudits: 0,
              formContent,
              historySource: "backend",
              historyAction: "create",
              historyUserID: slsLIUId,
              createdAt: new Date(
                new Date().getTime() - new Date().getTimezoneOffset() * 60000,
              )
                .toISOString()
                .slice(0, 19)
                .replace("T", " "),
            };

            if (editData?.id) {
              await dispatch(
                editAuditFormThunk({
                  id: editData.id,
                  ...payload,
                }),
              ).unwrap();
            } else {
              await dispatch(addAuditFormThunk(payload)).unwrap();
            }

            setErrors([]);
            form.resetFields();
            setParameters([]);
            resolve();
          } catch (error) {
            const errorMessage =
              error.data?.message ||
              "An error occurred while creating the auditForm";
            setErrors([errorMessage]);
            message.error(errorMessage);
            reject(error);
          }
        },
        onCancel() {
          reject();
          // User canceled
          console.log("Form submission canceled");
        },
      });
    });
  };

  useEffect(() => {
    // Setting type values
    const initialVals = {};
    parameters.forEach((param, index) => {
      initialVals[`type_${index}`] = "radio-group";
    });
    form.setFieldValue(initialVals);
  }, [parameters, form]);

  return (
    <>
      <AddEditForm
        id="antdformstyle"
        className="addAuditForm"
        form={form}
        editData={editData}
        onSubmit={onSubmit}
        onSuccess={onSuccess}
        successMessages={{
          create: "AuditForm created successfully",
          update: "AuditForm updated successfully",
        }}
        fields={
          <>
            <Row className="bodyWrapper" gutter={[12, 12]}>
              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  name="isWeightageForm"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Checkbox
                    onChange={handleCheckboxChange}
                    className="weightTage"
                  >
                    Is this a Weightage Form?
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={24} lg={24}>
                <Form.Item
                  className="m-0"
                  label="Process"
                  name="process"
                  rules={[
                    { required: true, message: "Please select a process!" },
                  ]}
                >
                  <Select
                    placeholder="Please select a process"
                    showSearch
                    allowClear
                    options={processes}
                    className=""
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        ?.toLowerCase()
                        .includes(input?.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
              <Col sm={24}>
                <label>Quality Parameters</label>
                <div className="parameters-container-wrap">
                  {parameters.map((param, index) => {
                    return (
                      <Row
                        className="parameterFieldsWrap"
                        key={index}
                        gutter={[12, 12]}
                      >
                        {/* Hidden Form Item for type */}
                        <Form.Item
                          name={`type_${index}`}
                          initialValue={param.type}
                          hidden
                        >
                          <span></span>
                        </Form.Item>
                        <Col
                          xs={24}
                          sm={24}
                          md={12}
                          lg={isWeightageForm ? 6 : 9}
                        >
                          <Form.Item
                            name={`displayText_${index}`}
                            label=""
                            rules={[
                              {
                                required: true,
                                message: "Please enter a title",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter a title"
                              value={param.displayText}
                              onChange={(e) =>
                                handleFieldChange(
                                  e.target.value,
                                  index,
                                  "displayText",
                                )
                              }
                            />
                          </Form.Item>
                        </Col>

                        {isWeightageForm && (
                          <Col xs={24} sm={24} md={12} lg={5}>
                            <Form.Item
                              name={`weightage_${index}`}
                              label=""
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter weightage",
                                },
                              ]}
                            >
                              <InputNumber
                                placeholder="Weightage"
                                value={param.weightage}
                                onChange={(value) =>
                                  handleFieldChange(value, index, "weightage")
                                }
                              />
                            </Form.Item>
                          </Col>
                        )}

                        <Col xs={24} sm={24} md={12} lg={5}>
                          <Form.Item
                            name={`critical_${index}`}
                            valuePropName="checked"
                            initialValue={false}
                            label=""
                          >
                            <Checkbox
                              checked={param.critical}
                              onChange={(e) => {
                                const isChecked = e.target.checked; // Get true/false based on checkbox state
                                handleFieldChange(isChecked, index, "critical"); // Pass true/false to handleFieldChange
                              }}
                            >
                              Critical
                            </Checkbox>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={6}>
                          <Form.Item
                            name={`required_${index}`}
                            valuePropName="checked"
                            initialValue={false} // Ensure the checkbox starts as unchecked
                            label=""
                          >
                            <Checkbox
                              checked={param.required} // Bind the checkbox value to param.required
                              onChange={(e) => {
                                const isChecked = e.target.checked; // Get true/false based on checkbox state
                                handleFieldChange(isChecked, index, "required"); // Pass true/false to handleFieldChange
                              }}
                            >
                              Mandatory
                            </Checkbox>
                          </Form.Item>
                        </Col>

                        <Col
                          xs={24}
                          sm={24}
                          md={12}
                          lg={isWeightageForm ? 2 : 4}
                        >
                          <Button
                            onClick={() => handleRemoveParameter(index)}
                            className="deleteBtn m-0"
                          >
                            <i className="fa fa-trash" aria-hidden="true"></i>
                          </Button>
                        </Col>
                      </Row>
                    );
                  })}
                  <Button
                    onClick={handleAddParameter}
                    //icon={<PlusOutlined />}
                    className="addParametersBtn"
                  >
                    + Add Parameter
                  </Button>
                </div>

                {error && (
                  <div style={{ color: "red", marginTop: "10px" }}>{error}</div>
                )}
              </Col>
            </Row>
          </>
        }
      />
    </>
  );
};
export default AddAuditForm;
